/**
 * TiltCard — subtle 3D mouse-tilt on hover (desktop only).
 *
 * - Max rotation ~ 7° X / Y
 * - Smooth spring transitions
 * - Disabled on touch + reduced-motion → renders a plain div
 *
 *   <TiltCard className="wit-card">…</TiltCard>
 */
import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import usePreferStaticUI from "@/lib/usePreferStaticUI";
import useIsFinePointer from "@/lib/useIsFinePointer";

const SPRING = { stiffness: 220, damping: 18, mass: 0.4 };

export const TiltCard = ({
  maxTilt = 7,
  scale = 1.01,
  className,
  testId,
  children,
}) => {
  const fine = useIsFinePointer();
  const reduced = usePreferStaticUI();
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [maxTilt, -maxTilt]), SPRING);
  const ry = useSpring(useTransform(mx, [0, 1], [-maxTilt, maxTilt]), SPRING);

  const enabled = fine && !reduced;

  const onMove = (e) => {
    if (!enabled || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { mx.set(0.5); my.set(0.5); };

  if (!enabled) {
    return (
      <div className={className} data-testid={testId}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{
        rotateX: rx,
        rotateY: ry,
        transformPerspective: 900,
        willChange: "transform",
      }}
      whileHover={{ scale }}
      transition={SPRING}
      className={className}
      data-testid={testId}
    >
      {children}
    </motion.div>
  );
};

export default TiltCard;
